<template>
  <div class="stats-pane">

    <!-- Period -->
    <div class="period-bar">
      <button
        v-for="p in periods" :key="p.id"
        @click="changePeriod(p.id)"
        :class="['period-btn', { active: period === p.id }]"
      >{{ p.label }}</button>
    </div>

    <div v-if="hasNav" class="nav-bar">
      <button @click="navPrev" class="nav-arrow">&lsaquo;</button>
      <span class="nav-label">{{ navLabel }}</span>
      <button @click="navNext" class="nav-arrow" :disabled="navNextDisabled">&rsaquo;</button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="stats-loading">
      <div class="spinner"></div>
      <p>Hämtar statistik...</p>
    </div>

    <template v-else>
      <!-- ── Kondition & aktiviteter ❤️ ── -->
      <div v-if="cardio && cardio.count" class="cardio-card">
        <h3>❤️ Kondition & aktiviteter</h3>
        <div class="cardio-kpis">
          <div class="cardio-kpi"><span>Aktiviteter</span><strong>{{ cardio.count }}</strong></div>
          <div class="cardio-kpi"><span>Distans</span><strong>{{ cardio.km.toFixed(1) }} km</strong></div>
          <div class="cardio-kpi"><span>Tid</span><strong>{{ Math.floor(cardio.minutes / 60) }}h {{ cardio.minutes % 60 }}m</strong></div>
          <div class="cardio-kpi"><span>Kcal</span><strong>{{ Math.round(cardio.kcal) }}</strong></div>
        </div>
        <div class="cardio-types">
          <div v-for="t in cardio.byType" :key="t.name" class="cardio-type-row">
            <span>{{ t.name }}</span>
            <span class="cardio-type-detail">{{ t.count }} st{{ t.km > 0.05 ? ` · ${t.km.toFixed(1)} km` : '' }} · {{ t.minutes }} min</span>
          </div>
        </div>
      </div>

      <div v-if="isEmpty" class="empty-state">Ingen träning loggad under vald period.</div>

      <template v-else>
        <!-- ── KPI cards with deltas ── -->
        <div v-if="sessionSummary" class="kpi-grid">
          <div v-for="k in kpis" :key="k.label" class="kpi">
            <span class="kpi-val">{{ k.value }}</span>
            <span class="kpi-label">{{ k.label }}</span>
            <span v-if="k.delta !== null" :class="['kpi-delta', k.delta > 0 ? 'up' : k.delta < 0 ? 'down' : 'flat']">
              {{ k.delta > 0 ? '▲' : k.delta < 0 ? '▼' : '•' }} {{ Math.abs(k.delta) }}%
            </span>
            <span v-else class="kpi-delta muted">–</span>
          </div>
        </div>

        <div v-if="sessionSummary" class="chip-row">
          <span class="chip">🔥 Streak: {{ sessionSummary.currentStreakWeeks }} v</span>
          <span class="chip">📅 {{ sessionSummary.sessionsPerWeek.toFixed(1) }} pass/vecka</span>
          <span class="chip">🏋️ {{ sessionSummary.totalSets }} set · {{ sessionSummary.totalReps }} reps</span>
        </div>

        <!-- ── Trend over time ── -->
        <div v-if="hasTrend" class="chart-card">
          <div class="card-head">
            <h4>Trend</h4>
            <div class="seg-toggle">
              <button v-for="m in trendMetrics" :key="m.id"
                :class="['seg-btn', { active: trendMetric === m.id }]"
                @click="setTrendMetric(m.id)">{{ m.label }}</button>
            </div>
          </div>
          <div class="chart-bar-wrap" style="height: 240px;"><canvas ref="trendCanvas"></canvas></div>
        </div>

        <!-- ── Per-exercise progression ── -->
        <div v-if="exerciseOptions.length" class="chart-card">
          <div class="card-head">
            <h4>Utveckling per övning</h4>
            <div class="seg-toggle">
              <button v-for="m in progressMetrics" :key="m.id"
                :class="['seg-btn', { active: progressMetric === m.id }]"
                @click="setProgressMetric(m.id)">{{ m.label }}</button>
            </div>
          </div>
          <select class="ex-select" :value="progressExId" @change="onPickExercise($event)">
            <option v-for="e in exerciseOptions" :key="e.exerciseTypeId" :value="e.exerciseTypeId">{{ e.exerciseTypeName }}</option>
          </select>
          <div v-if="progressLoading" class="mini-loading">Hämtar…</div>
          <div v-else-if="progressPoints.length < 2" class="chart-empty">
            För få datapunkter för den här övningen i perioden.
          </div>
          <div v-else class="chart-bar-wrap" style="height: 220px;"><canvas ref="progressCanvas"></canvas></div>
        </div>

        <!-- ── Distribution: muscles + exercises ── -->
        <template v-if="stats && stats.totalLifts > 0">
          <div class="metric-bar">
            <span class="metric-label">Fördelning:</span>
            <button @click="setMetric('count')"  :class="['metric-btn', { active: metric === 'count'  }]">Antal lyft</button>
            <button @click="setMetric('volume')" :class="['metric-btn', { active: metric === 'volume' }]">Volym</button>
          </div>

          <div class="charts-duo">
            <div class="chart-card">
              <h4>Muskelgrupper</h4>
              <div v-if="stats.muscleGroupStats.length === 0" class="chart-empty">Koppla muskler till övningar under Grunddata.</div>
              <div v-else class="chart-doughnut-wrap"><canvas ref="mgCanvas"></canvas></div>
            </div>
            <div class="chart-card">
              <h4>Muskler</h4>
              <div v-if="stats.muscleStats.length === 0" class="chart-empty">Koppla muskler till övningar under Grunddata.</div>
              <div v-else class="chart-doughnut-wrap"><canvas ref="mCanvas"></canvas></div>
            </div>
          </div>

          <div class="chart-card">
            <h4>Övningar</h4>
            <div class="chart-bar-wrap" :style="{ height: barChartHeight + 'px' }"><canvas ref="etCanvas"></canvas></div>
          </div>
          <p v-if="metric === 'volume'" class="fine-print">Volym fördelas jämnt över kopplade muskler, så summorna stämmer med totalen. Kroppsviktsövningar räknar din kroppsvikt.</p>
        </template>

        <!-- ── Consistency ── -->
        <div v-if="heatmapWeeks.length" class="chart-card">
          <h4>Aktivitet</h4>
          <div class="heatmap-scroll">
            <div class="heatmap">
              <div v-for="(col, ci) in heatmapWeeks" :key="ci" class="hm-col">
                <div v-for="cell in col" :key="cell.key"
                  class="hm-cell"
                  :class="{ empty: !cell.inRange || cell.count === 0 }"
                  :style="cell.inRange && cell.count > 0 ? { background: 'var(--gym-accent)', opacity: heatOpacity(cell.count) } : null"
                  :title="cell.inRange ? `${cell.date}: ${cell.count} pass` : ''"></div>
              </div>
            </div>
          </div>
          <div class="weekday-bars">
            <div v-for="(c, i) in (sessionSummary?.weekdayCounts || [])" :key="i" class="wd">
              <div class="wd-bar-wrap"><div class="wd-bar" :style="{ height: weekdayHeight(c) }"></div></div>
              <span class="wd-label">{{ WEEKDAYS[i] }}</span>
              <span class="wd-count">{{ c }}</span>
            </div>
          </div>
        </div>

        <!-- ── Highlights ── -->
        <div v-if="highlights" class="chart-card">
          <h4>Höjdpunkter</h4>
          <div class="hl-grid">
            <div class="hl" v-if="highlights.heaviestLift">
              <span class="hl-icon">🏋️</span>
              <div class="hl-body">
                <span class="hl-main">{{ highlights.heaviestLift.weight }} kg × {{ highlights.heaviestLift.reps }}</span>
                <span class="hl-sub">Tyngsta lyft · {{ highlights.heaviestLift.exerciseName }} · {{ formatDate(highlights.heaviestLift.date) }}</span>
              </div>
            </div>
            <div class="hl" v-if="highlights.biggestSession">
              <span class="hl-icon">📦</span>
              <div class="hl-body">
                <span class="hl-main">{{ fmtVol(highlights.biggestSession.value) }}</span>
                <span class="hl-sub">Största pass · {{ highlights.biggestSession.name }} · {{ formatDate(highlights.biggestSession.date) }}</span>
              </div>
            </div>
            <div class="hl" v-if="highlights.longestSession">
              <span class="hl-icon">⏱</span>
              <div class="hl-body">
                <span class="hl-main">{{ Math.round(highlights.longestSession.value) }} min</span>
                <span class="hl-sub">Längsta pass · {{ highlights.longestSession.name }} · {{ formatDate(highlights.longestSession.date) }}</span>
              </div>
            </div>
            <div class="hl" v-if="highlights.newPrCount > 0">
              <span class="hl-icon">🏆</span>
              <div class="hl-body">
                <span class="hl-main">{{ highlights.newPrCount }} nya PB</span>
                <span class="hl-sub">Uppskattade 1RM-rekord under perioden</span>
              </div>
            </div>
          </div>
        </div>

        <!-- ── Distance / time (cardio) ── -->
        <div v-if="hasCardio" class="chart-card cardio-card">
          <h4>Kondition</h4>
          <div class="cardio-row">
            <div v-if="sessionSummary.totalDistanceMeters > 0" class="cardio-stat">
              <span class="cardio-val">{{ fmtDistance(sessionSummary.totalDistanceMeters) }}</span>
              <span class="cardio-lbl">total distans</span>
            </div>
            <div v-if="sessionSummary.totalDurationSecondsMeasured > 0" class="cardio-stat">
              <span class="cardio-val">{{ formatHrs(sessionSummary.totalDurationSecondsMeasured / 60) }}</span>
              <span class="cardio-lbl">tidsbaserad träning</span>
            </div>
          </div>
        </div>
      </template>
    </template>

  </div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from 'vue';
import { gymAuthState, refreshIfNeeded } from '../services/gymTrackerAuth.js';
import api from '../services/gymTrackerApi.js';
import { theme } from '../services/gymTheme.js';
import {
  Chart,
  DoughnutController, ArcElement,
  BarController, LineController, CategoryScale, LinearScale, BarElement,
  PointElement, LineElement, Filler,
  Tooltip, Legend,
} from 'chart.js';

Chart.register(
  DoughnutController, ArcElement,
  BarController, LineController, CategoryScale, LinearScale, BarElement,
  PointElement, LineElement, Filler,
  Tooltip, Legend,
);

const PALETTE = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16',
  '#0EA5E9', '#D946EF', '#F43F5E', '#22C55E', '#A855F7',
];
const MONTHS_SV = ['Januari','Februari','Mars','April','Maj','Juni','Juli','Augusti','September','Oktober','November','December'];
const WEEKDAYS = ['Mån','Tis','Ons','Tor','Fre','Lör','Sön'];

function isoWeekNumber(d) {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  date.setUTCDate(date.getUTCDate() + 4 - (date.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
}
function mondayOf(d) {
  const copy = new Date(d);
  const dow = copy.getDay();
  copy.setDate(copy.getDate() - (dow === 0 ? 6 : dow - 1));
  copy.setHours(0, 0, 0, 0);
  return copy;
}

export default {
  name: 'GymStatsTab',
  setup() {
    const period  = ref('month');
    const metric  = ref('volume');
    const stats   = ref(null);
    const sessionSummary = ref(null);
    const loading = ref(false);
    const refDate = ref(new Date());

    const trendMetric = ref('volume');
    const trendMetrics = [
      { id: 'volume',   label: 'Volym',    unit: ' kg',   key: 'totalVolumeKg', type: 'line' },
      { id: 'sessions', label: 'Pass',     unit: '',      key: 'sessionCount',  type: 'bar' },
      { id: 'calories', label: 'Kalorier', unit: ' kcal', key: 'totalCalories', type: 'line' },
      { id: 'sets',     label: 'Set',      unit: '',      key: 'totalSets',     type: 'bar' },
    ];

    const progressMetric = ref('e1rm');
    const progressMetrics = [
      { id: 'e1rm',   label: 'Est. 1RM',  key: 'bestE1rm' },
      { id: 'weight', label: 'Toppvikt',  key: 'topWeight' },
      { id: 'volume', label: 'Volym',     key: 'totalVolume' },
    ];
    const progressExId = ref(null);
    const progressData = ref(null);
    const progressLoading = ref(false);

    const periods = [
      { id: 'week',     label: 'Vecka'  },
      { id: 'month',    label: 'Månad'  },
      { id: 'halfyear', label: 'Halvår' },
      { id: 'year',     label: 'År'     },
      { id: 'all',      label: 'Totalt' },
    ];

    const hasNav = computed(() => ['week', 'month', 'year'].includes(period.value));
    const navLabel = computed(() => {
      const d = refDate.value;
      if (period.value === 'year')  return `${d.getFullYear()}`;
      if (period.value === 'month') return `${MONTHS_SV[d.getMonth()]} ${d.getFullYear()}`;
      if (period.value === 'week') {
        const mon = mondayOf(d);
        const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
        const fmt = dt => `${dt.getDate()} ${MONTHS_SV[dt.getMonth()].slice(0, 3).toLowerCase()}`;
        return `V.${isoWeekNumber(mon)} · ${fmt(mon)}–${fmt(sun)} ${sun.getFullYear()}`;
      }
      return '';
    });
    const navNextDisabled = computed(() => {
      const now = new Date();
      if (period.value === 'year')  return refDate.value.getFullYear() >= now.getFullYear();
      if (period.value === 'month') {
        const d = refDate.value;
        return d.getFullYear() > now.getFullYear() || (d.getFullYear() === now.getFullYear() && d.getMonth() >= now.getMonth());
      }
      if (period.value === 'week') {
        const next = new Date(refDate.value); next.setDate(next.getDate() + 7);
        return mondayOf(next) > mondayOf(now);
      }
      return true;
    });
    function navPrev() {
      const d = new Date(refDate.value);
      if (period.value === 'week')  d.setDate(d.getDate() - 7);
      if (period.value === 'month') d.setMonth(d.getMonth() - 1);
      if (period.value === 'year')  d.setFullYear(d.getFullYear() - 1);
      refDate.value = d;
    }
    function navNext() {
      const d = new Date(refDate.value);
      if (period.value === 'week')  d.setDate(d.getDate() + 7);
      if (period.value === 'month') d.setMonth(d.getMonth() + 1);
      if (period.value === 'year')  d.setFullYear(d.getFullYear() + 1);
      refDate.value = d;
    }

    const isEmpty = computed(() =>
      (!stats.value || stats.value.totalLifts === 0) &&
      (!sessionSummary.value || sessionSummary.value.sessionCount === 0));
    const hasTrend = computed(() => (sessionSummary.value?.trend || []).some(b => b.sessionCount > 0));
    const highlights = computed(() => sessionSummary.value?.highlights || null);
    const hasCardio = computed(() =>
      (sessionSummary.value?.totalDistanceMeters > 0) || (sessionSummary.value?.totalDurationSecondsMeasured > 0));

    const exerciseOptions = computed(() =>
      [...(stats.value?.exerciseStats || [])].sort((a, b) => b.liftCount - a.liftCount));
    const progressPoints = computed(() => progressData.value?.points || []);

    const barChartHeight = computed(() => Math.max(200, (stats.value?.exerciseStats?.length ?? 0) * 40 + 60));

    // ── formatting ──
    function formatHrs(minutes) {
      const m = Math.round(minutes || 0);
      const h = Math.floor(m / 60);
      return h > 0 ? `${h}h ${m % 60}m` : `${m}m`;
    }
    function fmtVol(kg) {
      const v = Math.round(kg || 0);
      if (v >= 100000) return `${(v / 1000).toFixed(1).replace('.', ',')} t`;
      return `${v.toLocaleString('sv-SE')} kg`;
    }
    function fmtDistance(m) {
      return m >= 1000 ? `${(m / 1000).toFixed(2).replace('.', ',')} km` : `${Math.round(m)} m`;
    }
    function formatDate(d) { return new Date(d).toLocaleDateString('sv-SE'); }

    // ── KPI deltas ──
    function deltaPct(cur, prev) {
      if (prev == null || prev === 0) return null;
      return Math.round(((cur - prev) / prev) * 100);
    }
    const kpis = computed(() => {
      const s = sessionSummary.value; if (!s) return [];
      const p = s.previous;
      return [
        { label: 'Pass',       value: s.sessionCount,                    delta: deltaPct(s.sessionCount, p?.sessionCount) },
        { label: 'Total tid',  value: formatHrs(s.totalDurationMinutes), delta: deltaPct(s.totalDurationMinutes, p?.totalDurationMinutes) },
        { label: 'Volym',      value: fmtVol(s.totalVolumeKg),           delta: deltaPct(s.totalVolumeKg, p?.totalVolumeKg) },
        { label: 'Kalorier',   value: Math.round(s.totalCalories),       delta: deltaPct(s.totalCalories, p?.totalCalories) },
        { label: 'Snitt/pass', value: `${Math.round(s.avgDurationMinutes)} min`, delta: deltaPct(s.avgDurationMinutes, p?.avgDurationMinutes) },
      ];
    });

    // ── consistency heatmap ──
    function clientRange() {
      const d = refDate.value;
      if (period.value === 'week')  { const f = mondayOf(d); const t = new Date(f); t.setDate(f.getDate() + 7); return [f, t]; }
      if (period.value === 'month') { return [new Date(d.getFullYear(), d.getMonth(), 1), new Date(d.getFullYear(), d.getMonth() + 1, 1)]; }
      if (period.value === 'year')  { return [new Date(d.getFullYear(), 0, 1), new Date(d.getFullYear() + 1, 0, 1)]; }
      if (period.value === 'halfyear') { const t = new Date(); t.setHours(0,0,0,0); t.setDate(t.getDate() + 1); const f = new Date(t); f.setDate(t.getDate() - 183); return [f, t]; }
      // all → from first active day (or 1y back) to today
      const days = sessionSummary.value?.activeDays || [];
      const first = days.length ? new Date(days[0].date) : new Date(new Date().setFullYear(new Date().getFullYear() - 1));
      const t = new Date(); t.setHours(0,0,0,0); t.setDate(t.getDate() + 1);
      return [first, t];
    }
    const heatmapWeeks = computed(() => {
      const s = sessionSummary.value;
      if (!s || period.value === 'week') return []; // a single week is not worth a heatmap
      const [from, to] = clientRange();
      const counts = {};
      (s.activeDays || []).forEach(a => { counts[new Date(a.date).toISOString().slice(0, 10)] = a.count; });
      const cols = [];
      let cur = mondayOf(from);
      let guard = 0;
      while (cur < to && guard < 80) {
        const col = [];
        for (let i = 0; i < 7; i++) {
          const day = new Date(cur); day.setDate(cur.getDate() + i);
          const key = day.toISOString().slice(0, 10);
          col.push({ key, date: formatDate(day), count: counts[key] || 0, inRange: day >= from && day < to });
        }
        cols.push(col);
        cur = new Date(cur); cur.setDate(cur.getDate() + 7);
        guard++;
      }
      return cols;
    });
    function heatOpacity(count) { return Math.min(1, 0.4 + count * 0.3); }
    const weekdayMax = computed(() => Math.max(1, ...(sessionSummary.value?.weekdayCounts || [1])));
    function weekdayHeight(c) { return `${Math.round((c / weekdayMax.value) * 100)}%`; }

    // ── charts ──
    const trendCanvas = ref(null);
    const progressCanvas = ref(null);
    const mgCanvas = ref(null);
    const mCanvas  = ref(null);
    const etCanvas = ref(null);
    let chartInstances = {};

    function readTheme() {
      const el = document.querySelector('.gym-root');
      const cs = el ? getComputedStyle(el) : null;
      const v = (n, fb) => (cs && cs.getPropertyValue(n).trim()) || fb;
      return {
        text: v('--gym-text', '#1d2733'), muted: v('--gym-text-muted', '#5a6b7e'),
        border: v('--gym-border', '#e2e8f0'), surface: v('--gym-surface', '#fff'),
        accent: v('--gym-accent', '#3b82f6'),
      };
    }
    function destroyCharts() {
      Object.values(chartInstances).forEach(c => { try { c?.destroy(); } catch { /* */ } });
      chartInstances = {};
    }

    async function renderCharts() {
      await nextTick();
      destroyCharts();
      const tc = readTheme();

      // Trend
      if (trendCanvas.value && hasTrend.value) {
        const buckets = sessionSummary.value.trend;
        const cfg = trendMetrics.find(m => m.id === trendMetric.value);
        chartInstances.trend = new Chart(trendCanvas.value, {
          type: cfg.type,
          data: {
            labels: buckets.map(b => b.label),
            datasets: [{
              label: cfg.label,
              data: buckets.map(b => Math.round(b[cfg.key])),
              backgroundColor: cfg.type === 'bar' ? tc.accent + 'BB' : tc.accent + '33',
              borderColor: tc.accent,
              borderWidth: 2,
              borderRadius: 4,
              fill: cfg.type === 'line',
              tension: 0.3,
              pointRadius: buckets.length > 40 ? 0 : 3,
              pointBackgroundColor: tc.accent,
            }],
          },
          options: {
            responsive: true, maintainAspectRatio: false,
            plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ` ${ctx.parsed.y}${cfg.unit}` } } },
            scales: {
              x: { grid: { display: false }, ticks: { font: { size: 10 }, color: tc.muted, maxRotation: 0, autoSkip: true, maxTicksLimit: 12 } },
              y: { grid: { color: tc.border }, ticks: { font: { size: 10 }, color: tc.muted }, beginAtZero: true },
            },
          },
        });
      }

      // Progression
      if (progressCanvas.value && progressPoints.value.length >= 2) {
        const pts = progressPoints.value;
        const cfg = progressMetrics.find(m => m.id === progressMetric.value);
        chartInstances.progress = new Chart(progressCanvas.value, {
          type: 'line',
          data: {
            labels: pts.map(p => new Date(p.date).toLocaleDateString('sv-SE', { day: 'numeric', month: 'short' })),
            datasets: [{
              label: cfg.label,
              data: pts.map(p => Math.round(p[cfg.key])),
              borderColor: tc.accent, backgroundColor: tc.accent + '22',
              borderWidth: 2, fill: true, tension: 0.3,
              pointRadius: pts.length > 30 ? 0 : 3, pointBackgroundColor: tc.accent,
            }],
          },
          options: {
            responsive: true, maintainAspectRatio: false,
            plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ` ${ctx.parsed.y} kg` } } },
            scales: {
              x: { grid: { display: false }, ticks: { font: { size: 10 }, color: tc.muted, maxTicksLimit: 8 } },
              y: { grid: { color: tc.border }, ticks: { font: { size: 10 }, color: tc.muted } },
            },
          },
        });
      }

      if (!stats.value || stats.value.totalLifts === 0) return;
      const isVol = metric.value === 'volume';
      const getVal = s => isVol ? Math.round(s.totalVolume) : s.liftCount;
      const suffix = isVol ? ' kg' : ' lyft';
      const doughnutOptions = {
        responsive: true, maintainAspectRatio: true,
        plugins: {
          legend: { position: 'bottom', labels: { font: { size: 12 }, padding: 12, boxWidth: 13, color: tc.muted } },
          tooltip: { callbacks: { label: ctx => ` ${ctx.label}: ${ctx.parsed}${suffix}` } },
        },
      };
      if (mgCanvas.value && stats.value.muscleGroupStats.length > 0) {
        chartInstances.mg = new Chart(mgCanvas.value, {
          type: 'doughnut',
          data: { labels: stats.value.muscleGroupStats.map(s => s.muscleGroupName),
            datasets: [{ data: stats.value.muscleGroupStats.map(getVal), backgroundColor: PALETTE, borderWidth: 2, borderColor: tc.surface }] },
          options: doughnutOptions,
        });
      }
      if (mCanvas.value && stats.value.muscleStats.length > 0) {
        chartInstances.m = new Chart(mCanvas.value, {
          type: 'doughnut',
          data: { labels: stats.value.muscleStats.map(s => s.muscleName),
            datasets: [{ data: stats.value.muscleStats.map(getVal), backgroundColor: PALETTE, borderWidth: 2, borderColor: tc.surface }] },
          options: doughnutOptions,
        });
      }
      if (etCanvas.value && stats.value.exerciseStats.length > 0) {
        const sorted = [...stats.value.exerciseStats].sort((a, b) => getVal(b) - getVal(a));
        chartInstances.et = new Chart(etCanvas.value, {
          type: 'bar',
          data: { labels: sorted.map(s => s.exerciseTypeName),
            datasets: [{ label: isVol ? 'Volym (kg)' : 'Antal lyft', data: sorted.map(getVal),
              backgroundColor: tc.accent + 'BB', borderColor: tc.accent, borderWidth: 1, borderRadius: 4 }] },
          options: {
            indexAxis: 'y', responsive: true, maintainAspectRatio: false,
            plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => ` ${ctx.parsed.x}${suffix}` } } },
            scales: { x: { grid: { color: tc.border }, ticks: { font: { size: 11 }, color: tc.muted } },
              y: { grid: { display: false }, ticks: { font: { size: 12 }, color: tc.muted } } },
          },
        });
      }
    }

    // ── data ──
    async function loadProgress() {
      const exId = progressExId.value;
      if (!exId) { progressData.value = null; return; }
      progressLoading.value = true;
      try {
        await refreshIfNeeded();
        const d = await api.getExerciseProgress(exId, period.value, refDate.value.toISOString(), gymAuthState.token);
        progressData.value = d.exerciseProgress ?? null;
      } catch (e) {
        console.error('[GymStats] exerciseProgress failed:', e);
        progressData.value = null;
      } finally {
        progressLoading.value = false;
      }
      await renderCharts();
    }

    async function fetchStats() {
      if (!gymAuthState.loggedIn) return;
      loading.value = true;
      try {
        await refreshIfNeeded();
        const [d, sd] = await Promise.all([
          api.getWorkoutStats(period.value, refDate.value.toISOString(), gymAuthState.token),
          api.getSessionSummary(period.value, refDate.value.toISOString(), gymAuthState.token),
        ]);
        stats.value = d.workoutStats ?? null;
        sessionSummary.value = sd.gymSessionSummary ?? null;
      } catch (e) {
        console.error('[GymStats] fetchStats failed:', e);
        stats.value = null; sessionSummary.value = null;
      }
      loading.value = false;

      // Pick a default progression exercise (most-trained) and load it.
      const opts = exerciseOptions.value;
      if (!opts.some(o => o.exerciseTypeId === progressExId.value)) {
        progressExId.value = opts.length ? opts[0].exerciseTypeId : null;
      }
      await renderCharts();
      await loadProgress();
    }

    function onPickExercise(e) { progressExId.value = Number(e.target.value); loadProgress(); }
    function setTrendMetric(id) { trendMetric.value = id; renderCharts(); }
    function setProgressMetric(id) { progressMetric.value = id; renderCharts(); }
    function setMetric(m) { metric.value = m; renderCharts(); }
    function changePeriod(p) { period.value = p; refDate.value = new Date(); }

    // ── Kondition: aktiviteter i samma period ──
    const cardio = ref(null);

    function cardioRange() {
      const d = refDate.value;
      if (period.value === 'week') {
        const from = mondayOf(d);
        const to = new Date(from); to.setDate(from.getDate() + 7);
        return { from, to };
      }
      if (period.value === 'month') {
        const from = new Date(d.getFullYear(), d.getMonth(), 1);
        const to = new Date(d.getFullYear(), d.getMonth() + 1, 1);
        return { from, to };
      }
      if (period.value === 'year') {
        return { from: new Date(d.getFullYear(), 0, 1), to: new Date(d.getFullYear() + 1, 0, 1) };
      }
      if (period.value === 'halfyear') {
        const from = new Date(); from.setDate(from.getDate() - 182);
        return { from, to: null };
      }
      return { from: null, to: null };
    }

    async function loadCardio() {
      try {
        const { from, to } = cardioRange();
        if (!gymAuthState.loggedIn) return;
        await refreshIfNeeded();
        const d = await api.getActivities({
          from: from ? from.toISOString() : null,
          to: to ? to.toISOString() : null,
          limit: 500,
        }, gymAuthState.token);
        const list = d.activities || [];
        const byType = {};
        for (const a of list) {
          const key = a.activityType.nameSv;
          (byType[key] ??= { name: key, count: 0, km: 0, minutes: 0 });
          byType[key].count += 1;
          byType[key].km += (a.distanceMeters || 0) / 1000;
          byType[key].minutes += Math.round(a.durationSeconds / 60);
        }
        cardio.value = {
          count: list.length,
          km: list.reduce((acc, a) => acc + (a.distanceMeters || 0), 0) / 1000,
          minutes: Math.round(list.reduce((acc, a) => acc + a.durationSeconds, 0) / 60),
          kcal: list.reduce((acc, a) => acc + (a.estimatedKcal || 0), 0),
          byType: Object.values(byType).sort((a, b) => b.count - a.count).slice(0, 8),
        };
      } catch (e) { cardio.value = null; /* äldre server */ }
    }

    watch([period, refDate], () => { fetchStats(); loadCardio(); });
    onMounted(loadCardio);
    watch([() => theme.base, () => theme.accent], renderCharts);

    onMounted(fetchStats);
    onBeforeUnmount(destroyCharts);
    function reload() { fetchStats(); }

    return {
      cardio,
      period, metric, stats, sessionSummary, loading, periods, refDate,
      hasNav, navLabel, navNextDisabled, navPrev, navNext,
      isEmpty, hasTrend, highlights, hasCardio,
      kpis, barChartHeight,
      trendMetric, trendMetrics, setTrendMetric,
      progressMetric, progressMetrics, setProgressMetric, progressExId, onPickExercise,
      progressLoading, progressPoints, exerciseOptions,
      heatmapWeeks, heatOpacity, weekdayHeight, WEEKDAYS,
      trendCanvas, progressCanvas, mgCanvas, mCanvas, etCanvas,
      changePeriod, setMetric, reload,
      formatHrs, fmtVol, fmtDistance, formatDate,
    };
  },
};
</script>

<style scoped>
.stats-pane { display: flex; flex-direction: column; gap: 1.1rem; }

/* Period */
.period-bar { display: flex; gap: 0.4rem; flex-wrap: wrap; }
.period-btn {
  background: var(--gym-surface-2); border: 1px solid var(--gym-border);
  padding: 0.45rem 1rem; border-radius: var(--gym-radius-pill);
  font-size: 0.88rem; cursor: pointer; color: var(--gym-text-muted);
  transition: background var(--gym-dur) var(--gym-ease), color var(--gym-dur) var(--gym-ease), border-color var(--gym-dur) var(--gym-ease);
}
.period-btn:hover { background: var(--gym-accent-soft); border-color: var(--gym-accent-border); color: var(--gym-accent); }
.period-btn.active { background: var(--gym-accent); border-color: var(--gym-accent); color: var(--gym-on-accent); font-weight: 600; }

.nav-bar {
  display: flex; align-items: center; gap: 0.75rem;
  background: var(--gym-surface-2); border: 1px solid var(--gym-border);
  border-radius: var(--gym-radius); padding: 0.4rem 0.75rem; width: fit-content;
}
.nav-arrow { background: none; border: none; cursor: pointer; font-size: 1.6rem; line-height: 1; color: var(--gym-text-muted); padding: 0 0.2rem; border-radius: var(--gym-radius-sm); }
.nav-arrow:hover:not(:disabled) { color: var(--gym-accent); background: var(--gym-accent-soft); }
.nav-arrow:disabled { color: var(--gym-text-faint); cursor: not-allowed; }
.nav-label { font-size: 0.92rem; font-weight: 600; color: var(--gym-text); min-width: 14rem; text-align: center; }

/* Loading */
.stats-loading { text-align: center; padding: 2rem; color: var(--gym-text-muted); }
.spinner { width: 32px; height: 32px; border: 3px solid var(--gym-border); border-top-color: var(--gym-accent); border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto 1rem; }
@keyframes spin { to { transform: rotate(360deg); } }
.mini-loading { color: var(--gym-text-muted); font-size: 0.85rem; padding: 1rem 0; }
.empty-state { color: var(--gym-text-faint); font-size: 0.9rem; padding: 2rem 0; text-align: center; }

/* KPI */
.kpi-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 0.6rem; }
.kpi { background: var(--gym-surface); border: 1px solid var(--gym-border); border-radius: var(--gym-radius); padding: 0.8rem 0.5rem; display: flex; flex-direction: column; align-items: center; gap: 0.15rem; box-shadow: var(--gym-shadow-sm); }
.kpi-val { font-size: 1.2rem; font-weight: 800; color: var(--gym-accent); text-align: center; }
.kpi-label { font-size: 0.7rem; color: var(--gym-text-muted); text-align: center; }
.kpi-delta { font-size: 0.7rem; font-weight: 700; }
.kpi-delta.up { color: var(--gym-success); }
.kpi-delta.down { color: var(--gym-danger); }
.kpi-delta.flat, .kpi-delta.muted { color: var(--gym-text-faint); }
@media (max-width: 640px) { .kpi-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 380px) { .kpi-grid { grid-template-columns: repeat(2, 1fr); } }

.chip-row { display: flex; gap: 0.5rem; flex-wrap: wrap; }
.chip { background: var(--gym-surface-3); color: var(--gym-text-muted); padding: 0.35rem 0.8rem; border-radius: var(--gym-radius-pill); font-size: 0.82rem; font-weight: 600; }

/* Metric */
.metric-bar { display: flex; align-items: center; gap: 0.5rem; flex-wrap: wrap; }
.metric-label { font-size: 0.85rem; color: var(--gym-text-muted); }
.metric-btn { background: var(--gym-surface-2); border: 1px solid var(--gym-border); padding: 0.4rem 0.9rem; border-radius: var(--gym-radius-sm); font-size: 0.85rem; cursor: pointer; color: var(--gym-text-muted); }
.metric-btn:hover { background: var(--gym-accent-soft); color: var(--gym-accent); }
.metric-btn.active { background: var(--gym-accent); border-color: var(--gym-accent); color: var(--gym-on-accent); font-weight: 600; }

/* Chart cards */
.chart-card { background: var(--gym-surface); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-lg); padding: 1.25rem; box-shadow: var(--gym-shadow-sm); }
.chart-card h4 { margin: 0 0 1rem; font-size: 0.95rem; color: var(--gym-text); font-weight: 700; border-bottom: 1px solid var(--gym-border); padding-bottom: 0.6rem; }
.card-head { display: flex; align-items: center; justify-content: space-between; gap: 0.6rem; margin-bottom: 1rem; flex-wrap: wrap; }
.card-head h4 { margin: 0; border: none; padding: 0; }
.chart-empty { color: var(--gym-text-faint); font-size: 0.85rem; font-style: italic; }
.charts-duo { display: grid; grid-template-columns: repeat(auto-fit, minmax(260px, 1fr)); gap: 1.1rem; }
.chart-doughnut-wrap { max-width: 340px; margin: 0 auto; }
.chart-bar-wrap { position: relative; width: 100%; }
.fine-print { font-size: 0.75rem; color: var(--gym-text-faint); margin: 0; }

/* Segmented toggle */
.seg-toggle { display: inline-flex; background: var(--gym-surface-2); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); padding: 0.2rem; gap: 0.15rem; flex-wrap: wrap; }
.seg-btn { background: none; border: none; padding: 0.35rem 0.7rem; border-radius: 7px; font-size: 0.8rem; color: var(--gym-text-muted); cursor: pointer; transition: background var(--gym-dur) var(--gym-ease), color var(--gym-dur) var(--gym-ease); }
.seg-btn.active { background: var(--gym-accent); color: var(--gym-on-accent); font-weight: 600; }

.ex-select { width: 100%; padding: 0.55rem 0.7rem; border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); background: var(--gym-surface-2); color: var(--gym-text); font-size: 0.9rem; margin-bottom: 0.85rem; }
.ex-select:focus { outline: none; border-color: var(--gym-accent); box-shadow: 0 0 0 3px var(--gym-accent-soft); }

/* Heatmap */
.heatmap-scroll { overflow-x: auto; padding-bottom: 0.4rem; }
.heatmap { display: flex; gap: 3px; min-width: min-content; }
.hm-col { display: flex; flex-direction: column; gap: 3px; }
.hm-cell { width: 13px; height: 13px; border-radius: 3px; background: var(--gym-accent); }
.hm-cell.empty { background: var(--gym-surface-3); opacity: 1; }
.weekday-bars { display: flex; gap: 0.4rem; margin-top: 1.1rem; align-items: flex-end; }
.wd { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 0.25rem; }
.wd-bar-wrap { width: 100%; height: 60px; display: flex; align-items: flex-end; }
.wd-bar { width: 100%; background: var(--gym-accent); border-radius: 4px 4px 0 0; min-height: 3px; transition: height var(--gym-dur-lg) var(--gym-ease); }
.wd-label { font-size: 0.68rem; color: var(--gym-text-muted); }
.wd-count { font-size: 0.72rem; font-weight: 700; color: var(--gym-text); }

/* Highlights */
.hl-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr)); gap: 0.7rem; }
.hl { display: flex; gap: 0.7rem; align-items: center; background: var(--gym-surface-2); border: 1px solid var(--gym-border); border-radius: var(--gym-radius); padding: 0.7rem 0.85rem; }
.hl-icon { font-size: 1.4rem; }
.hl-body { display: flex; flex-direction: column; gap: 0.1rem; min-width: 0; }
.hl-main { font-size: 1.02rem; font-weight: 800; color: var(--gym-text); }
.hl-sub { font-size: 0.75rem; color: var(--gym-text-muted); }

/* Cardio */
.cardio-row { display: flex; gap: 1.5rem; flex-wrap: wrap; }
.cardio-stat { display: flex; flex-direction: column; }
.cardio-val { font-size: 1.5rem; font-weight: 800; color: var(--gym-accent); }
.cardio-lbl { font-size: 0.78rem; color: var(--gym-text-muted); }

/* ── Kondition & aktiviteter ── */
.cardio-card {
  background: var(--gym-surface); border: 1px solid var(--gym-border);
  border-radius: var(--gym-radius-lg); padding: 1.1rem;
  display: flex; flex-direction: column; gap: 0.8rem; box-shadow: var(--gym-shadow-sm);
}
.cardio-card h3 { margin: 0; font-size: 1.02rem; color: var(--gym-text); }
.cardio-kpis { display: flex; gap: 0.5rem; }
.cardio-kpi {
  flex: 1; display: flex; flex-direction: column; align-items: center; gap: 0.15rem;
  background: var(--gym-surface-2); border-radius: var(--gym-radius-sm); padding: 0.5rem;
}
.cardio-kpi span { font-size: 0.72rem; color: var(--gym-text-muted); }
.cardio-kpi strong { font-size: 0.95rem; color: var(--gym-text); }
.cardio-types { display: flex; flex-direction: column; gap: 0.3rem; }
.cardio-type-row {
  display: flex; justify-content: space-between; font-size: 0.88rem; color: var(--gym-text);
}
.cardio-type-detail { color: var(--gym-text-muted); }
</style>
